import React, { PropsWithChildren, ReactElement, useState, useCallback } from 'react';
import theme from './AttachmentList.module.scss';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { without } from 'lodash-es';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { getUniqueId, classnames } from '@/utils/html';
import { faUpload } from '@fortawesome/pro-light-svg-icons';
import { AttachmentLike, AttachmentListPropsWithUrls } from '.';
import { FileUpload } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OptionalId, ChangedItem, FileMetadata } from '@/types';
import FileUploadWizard, { BuyerFileUploadWizard } from '@/components/FileUploadWizard/FileUploadUnitDocuments';
import { useIsBuyer } from '@/utils/hooks';
import AttachmentListItem from './AttachmentListItem';
import PasteListenerWrapper from './PasteListener';

function AttachmentList<T extends OptionalId<AttachmentLike>>({
    value,
    onChange,
    onItemChange,
    canAdd = true,
    canEdit = true,
    canDelete = true,
    allowPaste = false,
    showVisibilityFlags = false,
    useUploadWizard = false,
    urlGenerator,
    ownerProfileId,
    residentialUnitId,
    itemsPerRow = 2,
    editMode,
}: PropsWithChildren<AttachmentListPropsWithUrls<T>>): ReactElement | null {
    const [isOpen, setIsOpen] = useState(false);
    const isBuyer = useIsBuyer();

    const handleFileDrop = useCallback(
        (files: any[]) => {
            var addedAttachments: any[] = files
                .map((x) => (x instanceof FileData ? x : new FileData(x)))
                .map((file) => ({
                    name: file.fileName,
                    fileData: file,
                    fileMetadata: FileMetadata.fromFileData(file),
                    _localId: getUniqueId(),
                }));

            triggerHandler(onChange, [...value, ...addedAttachments]);
        },
        [onChange, value]
    );

    const handleAttachmentChange = (change: ChangedItem<T>) => {
        triggerHandler(onChange, change.updateArray(value));
        triggerHandler(onItemChange, change);
    };

    const handleAttachmentDelete = (attachment: T) => {
        triggerHandler(onChange, without(value, attachment));
    };

    const handlePaste = useCallback(
        (e: ClipboardEvent) => {
            if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
                handleFileDrop(Array.from(e.clipboardData.files));
                e.preventDefault();
            }
        },
        [handleFileDrop]
    );

    if (value.length === 0 && !canEdit && !canAdd) {
        return null;
    }

    const itemWidth = `calc(100% / ${itemsPerRow} - 20px)`;

    return (
        <ul className={classnames(theme.list)}>
            {allowPaste && canAdd && <PasteListenerWrapper handler={handlePaste} />}
            {value.map((att) => (
                <li key={att.id || (att as any)._localId} style={{ flexBasis: itemWidth }}>
                    <AttachmentListItem
                        att={att}
                        canEdit={canEdit}
                        openUrl={urlGenerator ? urlGenerator(att) : undefined}
                        // downloadUrl={urlGenerator ? urlGenerator(att) : undefined}
                        showVisibilityFlags={showVisibilityFlags}
                        onChange={handleAttachmentChange}
                        onDelete={canDelete ? handleAttachmentDelete : undefined}
                        ownerProfileId={ownerProfileId}
                        editMode={editMode}
                    />
                </li>
            ))}

            {canAdd && (
                <li className={theme.fileUpload} style={{ flexBasis: itemWidth }}>
                    {useUploadWizard ? (
                        <>
                            <div onClick={() => setIsOpen(true)} className={theme.fileUploadButton}>
                                <FontAwesomeIcon size="4x" icon={faUpload} />
                                <Trans>Add attachment</Trans>
                            </div>
                            {/* <Button color="tertiary" onClick={() => setIsOpen(true)}>
                                <Trans>Upload</Trans>
                            </Button> */}
                            {isBuyer ? (
                                <BuyerFileUploadWizard
                                    toggle={() => setIsOpen(false)}
                                    isOpen={isOpen}
                                    onFileChange={handleFileDrop}
                                />
                            ) : (
                                <FileUploadWizard
                                    residentialUnitId={residentialUnitId!}
                                    toggle={() => setIsOpen(false)}
                                    isOpen={isOpen}
                                    onFileChange={handleFileDrop}
                                />
                            )}
                        </>
                    ) : (
                        <FileUpload onDrop={handleFileDrop} />
                    )}
                </li>
            )}
        </ul>
    );
}

export default AttachmentList;
